// Edit Poll Web Component
// Encapsulated component for creating/editing polls in post-writer

class EditPoll extends HTMLElement {
  constructor() {
    super();
    this.pollQuestion = '';
    this.pollOptions = ['', '']; // Start with 2 empty options
    this.pollType = 'single'; // Default to single-selection
    this.selectedPrompt = null;
    this.isSubmitting = false;
  }

  connectedCallback() {
    this.render();
    this.attachEventListeners();
  }

  static get observedAttributes() {
    return ['prompt-data'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'prompt-data' && newValue) {
      try {
        const prompt = JSON.parse(newValue);
        this.loadPromptData(prompt);
      } catch (error) {
        window.logger?.error('Invalid prompt data in edit-poll:', error);
      }
    }
  }

  loadPromptData(prompt) {
    if (prompt.prompt_type === 'poll') {
      this.selectedPrompt = prompt;
      this.pollQuestion = prompt.title || '';
      if (prompt.poll_options && Array.isArray(prompt.poll_options)) {
        this.pollOptions = [...prompt.poll_options];
        // Ensure we have at least 2 options
        while (this.pollOptions.length < 2) {
          this.pollOptions.push('');
        }
      }
      this.render();
      this.attachEventListeners();
    }
  }

  setPromptData(prompt) {
    this.loadPromptData(prompt);
  }

  render() {
    this.innerHTML = `
      <div class="edit-poll">
        <div class="poll-header">
          <post-type-selector selected-type="poll" id="pollPostTypeSelector"></post-type-selector>
          <div class="poll-prompt-actions">
            <washing-machine-icon 
              id="pollWashBtn" 
              size="medium" 
              interactive="true"
              title="Get a random poll prompt"
            ></washing-machine-icon>
            <button type="button" class="propose-prompt-btn" id="pollProposeBtn" title="Propose a new poll prompt">
              💡
            </button>
          </div>
        </div>
        
        <div class="form-group">
          <label for="pollType">Poll Type</label>
          <div class="poll-type-selector">
            <label class="poll-type-option">
              <input 
                type="radio" 
                name="pollType" 
                value="single" 
                ${this.pollType === 'single' ? 'checked' : ''}
              />
              <span>Single</span>
            </label>
            <label class="poll-type-option">
              <input 
                type="radio" 
                name="pollType" 
                value="multi" 
                ${this.pollType === 'multi' ? 'checked' : ''}
              />
              <span>Multi-Select</span>
            </label>
            <label class="poll-type-option">
              <input 
                type="radio" 
                name="pollType" 
                value="toggle" 
                ${this.pollType === 'toggle' ? 'checked' : ''}
              />
              <span>Toggle</span>
            </label>
          </div>
        </div>
        
        <div class="form-group">
          <label for="pollQuestion">Poll Question</label>
          <textarea 
            id="pollQuestion"
            class="form-control"
            placeholder="What's your favorite season?"
            maxlength="500"
            rows="2"
            required
          >${this.escapeHtml(this.pollQuestion)}</textarea>
          <div class="char-count">
            <span id="questionCharCount">${this.pollQuestion.length}</span>/500
          </div>
        </div>

        <div class="form-group">
          <label>Poll Options <span class="option-count">(${this.pollOptions.length}/10)</span></label>
          <div class="poll-options-list" id="pollOptionsList">
            ${this.pollOptions.map((option, index) => `
              <div class="poll-option-item" data-index="${index}">
                <input 
                  type="text"
                  class="form-control poll-option-input"
                  data-index="${index}"
                  placeholder="Option ${index + 1}"
                  value="${this.escapeHtml(option)}"
                  maxlength="500"
                  required
                />
                ${this.pollOptions.length > 2 ? `
                  <button 
                    type="button" 
                    class="btn btn-icon remove-option-btn"
                    data-index="${index}"
                    title="Remove option"
                  >
                    ✕
                  </button>
                ` : ''}
              </div>
            `).join('')}
          </div>
          ${this.pollOptions.length < 10 ? `
            <button type="button" class="btn btn-secondary add-option-btn" id="addOptionBtn">
              + Add Option
            </button>
          ` : ''}
        </div>

        <div class="poll-actions">
          <button type="button" class="btn btn-secondary" id="clearPollBtn">Clear</button>
          <button type="button" class="btn btn-primary" id="createPollBtn">
            <span class="btn-text">Create Poll</span>
            <span class="spinner hidden"></span>
          </button>
        </div>

        <div class="alert-container"></div>
      </div>
    `;
  }

  attachEventListeners() {
    // Post type selector
    const postTypeSelector = this.querySelector('#pollPostTypeSelector');
    if (postTypeSelector) {
      postTypeSelector.addEventListener('post-type-selected', (e) => {
        // Dispatch event to parent post-writer to switch types
        this.dispatchEvent(new CustomEvent('switch-post-type', {
          detail: { type: e.detail.type },
          bubbles: true
        }));
      });
    }

    // Poll type selector
    this.querySelectorAll('input[name="pollType"]').forEach(radio => {
      radio.addEventListener('change', (e) => {
        this.pollType = e.target.value;
      });
    });

    // Question input
    const questionInput = this.querySelector('#pollQuestion');
    questionInput.addEventListener('input', (e) => {
      this.pollQuestion = e.target.value;
      this.updateQuestionCharCount();
    });

    // Option inputs
    this.querySelectorAll('.poll-option-input').forEach(input => {
      input.addEventListener('input', (e) => {
        const index = parseInt(e.target.dataset.index, 10);
        this.pollOptions[index] = e.target.value;
        this.validateOptions();
      });
    });

    // Remove option buttons
    this.querySelectorAll('.remove-option-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const index = parseInt(e.target.dataset.index, 10);
        this.removeOption(index);
      });
    });

    // Add option button
    const addOptionBtn = this.querySelector('#addOptionBtn');
    if (addOptionBtn) {
      addOptionBtn.addEventListener('click', () => {
        this.addOption();
      });
    }

    // Clear button
    const clearBtn = this.querySelector('#clearPollBtn');
    clearBtn.addEventListener('click', () => {
      this.clearPoll();
    });

    // Create poll button
    const createBtn = this.querySelector('#createPollBtn');
    createBtn.addEventListener('click', () => {
      this.createPoll();
    });

    // Washing machine: random poll prompt
    const washBtn = this.querySelector('#pollWashBtn');
    if (washBtn) {
      washBtn.addEventListener('click', () => {
        this.getRandomPollPrompt();
      });
    }

    // Propose poll prompt
    const proposeBtn = this.querySelector('#pollProposeBtn');
    if (proposeBtn) {
      proposeBtn.addEventListener('click', () => {
        document.dispatchEvent(new CustomEvent('open-propose-prompt', {
          detail: { presetType: 'poll' },
          bubbles: true
        }));
      });
    }
  }

  async getRandomPollPrompt() {
    const washBtn = this.querySelector('#pollWashBtn');
    if (washBtn && washBtn.spin) {
      washBtn.spin();
    }
    try {
      const res = await window.API.prompts.getPublic();
      const all = res.prompts || [];
      const pollPrompts = all.filter((p) => p.prompt_type === 'poll');
      if (pollPrompts.length === 0) {
        this.showAlert('No poll prompts yet. Propose one with 💡!', 'info');
        return;
      }
      const prompt = pollPrompts[Math.floor(Math.random() * pollPrompts.length)];
      setTimeout(() => {
        this.setPromptData(prompt);
      }, 600);
    } catch (err) {
      window.logger?.error('Failed to fetch poll prompts:', err);
      this.showAlert('Could not load poll prompts. Try again.', 'error');
    }
  }

  updateQuestionCharCount() {
    const charCount = this.querySelector('#questionCharCount');
    if (charCount) {
      charCount.textContent = this.pollQuestion.length;

      charCount.classList.remove('char-count-ok', 'char-count-warning', 'char-count-error');
      if (this.pollQuestion.length > 450) {
        charCount.classList.add('char-count-error');
      } else if (this.pollQuestion.length > 400) {
        charCount.classList.add('char-count-warning');
      } else {
        charCount.classList.add('char-count-ok');
      }
    }
  }

  addOption() {
    if (this.pollOptions.length >= 10) {
      this.showAlert('Maximum 10 options allowed', 'error');
      return;
    }
    this.pollOptions.push('');
    this.render();
    this.attachEventListeners();

    // Focus the new input
    const newInput = this.querySelector(`.poll-option-input[data-index="${this.pollOptions.length - 1}"]`);
    if (newInput) {
      setTimeout(() => newInput.focus(), 100);
    }
  }

  removeOption(index) {
    if (this.pollOptions.length <= 2) {
      this.showAlert('Polls must have at least 2 options', 'error');
      return;
    }
    this.pollOptions.splice(index, 1);
    this.render();
    this.attachEventListeners();
  }

  validateOptions() {
    // Check for duplicates (case-insensitive)
    const optionTexts = this.pollOptions
      .map(opt => opt.trim().toLowerCase())
      .filter(opt => opt.length > 0);

    const uniqueOptions = new Set(optionTexts);
    if (optionTexts.length !== uniqueOptions.size) {
      // Duplicate found - highlight inputs
      this.highlightDuplicates();
      return false;
    }

    this.clearDuplicateHighlights();
    return true;
  }

  highlightDuplicates() {
    const optionInputs = this.querySelectorAll('.poll-option-input');
    const optionTexts = this.pollOptions.map(opt => opt.trim().toLowerCase());

    optionInputs.forEach((input, index) => {
      const text = this.pollOptions[index].trim().toLowerCase();
      if (text && optionTexts.filter(t => t === text).length > 1) {
        input.classList.add('duplicate');
      }
    });
  }

  clearDuplicateHighlights() {
    this.querySelectorAll('.poll-option-input').forEach(input => {
      input.classList.remove('duplicate');
    });
  }

  clearPoll() {
    this.pollQuestion = '';
    this.pollOptions = ['', ''];
    this.pollType = 'single';
    this.selectedPrompt = null;
    this.render();
    this.attachEventListeners();

    // Focus question input
    setTimeout(() => {
      const questionInput = this.querySelector('#pollQuestion');
      if (questionInput) {
        questionInput.focus();
      }
    }, 100);
  }

  async createPoll() {
    if (this.isSubmitting) return;

    // Validate question
    const trimmedQuestion = this.pollQuestion.trim();
    if (!trimmedQuestion || trimmedQuestion.length === 0) {
      this.showAlert('Please enter a poll question', 'error');
      return;
    }

    if (trimmedQuestion.length > 500) {
      this.showAlert('Poll question must be 500 characters or less', 'error');
      return;
    }

    // Validate options
    const validOptions = this.pollOptions
      .map(opt => opt.trim())
      .filter(opt => opt.length > 0);

    if (validOptions.length < 2) {
      this.showAlert('Polls must have at least 2 options', 'error');
      return;
    }

    if (validOptions.length > 10) {
      this.showAlert('Polls can have at most 10 options', 'error');
      return;
    }

    // Check for duplicates
    const optionTexts = validOptions.map(opt => opt.toLowerCase());
    const uniqueOptions = new Set(optionTexts);
    if (optionTexts.length !== uniqueOptions.size) {
      this.showAlert('Poll options must be unique', 'error');
      this.highlightDuplicates();
      return;
    }

    // Check option length
    for (const option of validOptions) {
      if (option.length > 500) {
        this.showAlert('Each option must be 500 characters or less', 'error');
        return;
      }
    }

    this.setSubmitting(true);
    this.clearAlerts();

    try {
      const postData = {
        post_type: 'poll',
        poll_question: trimmedQuestion,
        poll_options: validOptions,
        poll_type: this.pollType
      };

      if (this.selectedPrompt) {
        postData.promptId = this.selectedPrompt.id;
      }

      Object.assign(postData, this.closest('post-writer')?.getPostStylePayload?.() || {});

      const post = await window.API.posts.create(postData);

      // Clear the form
      this.clearPoll();

      // Dispatch event to notify parent component
      this.dispatchEvent(new CustomEvent('poll-created', {
        detail: { pollPost: post },
        bubbles: true
      }));

      // Show success feedback
      this.showAlert('Poll created successfully! 🎉', 'success');

    } catch (error) {
      window.logger.error('Error creating poll:', error);
      let errorMessage = 'Failed to create poll. Please try again.';

      if (error.message) {
        errorMessage = error.message;
      } else if (error.details && Array.isArray(error.details)) {
        errorMessage = error.details.map(d => d.message).join(', ');
      }

      this.showAlert(errorMessage, 'error');
      const stylePicker = this.closest('post-writer')?.querySelector('#postStylePicker');
      if (stylePicker?.getStyle?.() && stylePicker.showError) {
        stylePicker.showError(errorMessage);
      }
    } finally {
      this.setSubmitting(false);
    }
  }

  setSubmitting(isSubmitting) {
    this.isSubmitting = isSubmitting;
    const createBtn = this.querySelector('#createPollBtn');
    const btnText = this.querySelector('.btn-text');
    const spinner = this.querySelector('.spinner');

    if (isSubmitting) {
      createBtn.disabled = true;
      btnText.textContent = 'Creating...';
      spinner.classList.remove('hidden');
    } else {
      createBtn.disabled = false;
      btnText.textContent = 'Create Poll';
      spinner.classList.add('hidden');
    }
  }

  showAlert(message, type = 'info') {
    const container = this.querySelector('.alert-container');
    const alert = document.createElement('div');
    alert.className = `alert alert-${type}`;
    alert.textContent = message;

    container.appendChild(alert);

    // Auto-remove after 5 seconds
    setTimeout(() => {
      if (alert.parentNode) {
        alert.remove();
      }
    }, 5000);
  }

  clearAlerts() {
    const container = this.querySelector('.alert-container');
    container.innerHTML = '';
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

// Register the custom element
customElements.define('edit-poll', EditPoll);
